import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// FIX 5: Delete Order Dialog
// Allows permanent deletion of PENDING orders that haven't been processed

const DeleteOrderDialog = ({ open, onClose, order, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
      setDeleting(false);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for deletion');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      // Call the delete API
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'}/orders/${order.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Client-Version': '2.0.0',
            'X-HIPAA-Compliant': 'true'
          },
          body: JSON.stringify({ reason: reason.trim() })
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        onSuccess(result);
        onClose();
      } else {
        setError(result.message || 'Failed to delete order');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the order');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      setReason('');
      setError('');
      onClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return '#f59e0b';
      case 'CONFIRMED': return '#3b82f6';
      case 'PROCESSING': return '#8b5cf6';
      case 'PICKING': return '#6366f1';
      default: return '#6b7280';
    }
  };

  const canDelete = order?.status === 'PENDING';
  const pickedCount = order?.items?.reduce((sum, item) => sum + (item.pickedQuantity || 0), 0) || 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderTop: '4px solid #dc2626' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#dc2626' }}>
        <DeleteIcon />
        Permanently Delete Order {order?.orderNumber}?
      </DialogTitle>
      <DialogContent>
        <Alert 
          severity="error" 
          icon={<WarningIcon />}
          sx={{ mb: 2, mt: 1 }}
        >
          <Typography variant="body2" fontWeight={600}>
            This action cannot be undone.
          </Typography>
          <Typography variant="body2">
            The order will be permanently removed from the system. Only use this for orders created in error that haven't been processed.
          </Typography>
        </Alert>

        {/* Order Info */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Order Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <Chip 
                  label={order?.status} 
                  size="small" 
                  sx={{ 
                    bgcolor: getStatusColor(order?.status), 
                    color: 'white',
                    fontWeight: 600 
                  }} 
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Client</Typography>
              <Typography variant="body2" fontWeight={500}>
                {order?.user?.firstName} {order?.user?.lastName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="body2" fontWeight={500}>
                ${Number(order?.totalAmount || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Warning for non-PENDING orders */}
        {!canDelete && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Cannot delete:</strong> Only PENDING orders can be deleted. 
              This order is in <strong>{order?.status}</strong> status.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Use "Cancel Order" instead to update status and restore inventory.
            </Typography>
          </Alert>
        )}

        {/* Warning for picked items */}
        {canDelete && pickedCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This order has {pickedCount} picked item(s). 
              Deleting will NOT restore these items to inventory.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Consider using "Cancel Order" instead to properly restore inventory.
            </Typography>
          </Alert>
        )}

        {/* Items list */}
        {order?.items?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Items in this order:
            </Typography>
            <List dense sx={{ bgcolor: '#f8fafc', borderRadius: 1 }}>
              {order.items.map((item, idx) => (
                <ListItem key={item.id || idx}>
                  <ListItemText
                    primary={`${item.quantity}× ${item.product?.name || item.product || 'Unknown Product'}`}
                    secondary={`$${Number(item.unitPrice || 0).toFixed(2)} each • ${item.pickedQuantity || 0} picked`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {canDelete && (
          <TextField
            label="Reason for Deletion *"
            multiline
            rows={2}
            fullWidth
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="e.g., Duplicate order created by mistake"
            error={!!error}
            helperText={error || 'This reason will be recorded in the audit log.'}
            disabled={deleting}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={deleting}
          variant="outlined"
        >
          Keep Order
        </Button>
        {canDelete && (
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting || !reason.trim()}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteOrderDialog;

